import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { LevelProvider } from './context/LevelContext';
import { useLevel } from './context/LevelContext';
import { Navigation } from './components/Navigation';
import { LandingPage } from './pages/LandingPage';
import { RegisterPage } from './pages/RegisterPage';
import { LessonPage } from './pages/LessonPage';
import { LessonPreviewPage } from './pages/LessonPreviewPage';
import { TicketCreatePage } from './pages/TicketCreatePage';
import { AdminTicketsPage } from './pages/AdminTicketsPage';
import { TelegramLinkPage } from './pages/TelegramLinkPage';
import { StudentLevelPage } from './components/StudentLevelPage';
import { AdminPanel } from './components/AdminPanel';
import { buildLevelPath } from './utils/routeLanguage';
import './styles/global-design.css';
import './App.css';

const LoadingComponent = () => {
  const { t } = useLanguage();
  return <div className="text-center p-8">{t('loading')}</div>;
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingComponent />;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return children;
};

const StaffRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { language } = useLanguage();
  const { selectedLevel } = useLevel();
  const levelPath = buildLevelPath(language, selectedLevel);

  if (loading) {
    return <LoadingComponent />;
  }

  if (!user || (user.role !== 'admin' && user.role !== 'teacher')) {
    return <Navigate to={levelPath} replace />;
  }

  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { language } = useLanguage();
  const { selectedLevel } = useLevel();
  const levelPath = buildLevelPath(language, selectedLevel);

  if (loading) {
    return <LoadingComponent />;
  }

  if (!user || user.role !== 'admin') {
    return <Navigate to={levelPath} replace />;
  }

  return children;
};

const AppContent = () => {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { t, language } = useLanguage();
  const { selectedLevel } = useLevel();
  const location = useLocation();
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register';
  const levelPath = buildLevelPath(language, selectedLevel);

  React.useEffect(() => {
    document.title = t('pageTitle') || 'LinguaDE';
  }, [t]);

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
      {user && !isAuthPage && <Navigation />}
      <Routes>
          <Route path="/login" element={<LandingPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route
          path="/:lang/:level"
          element={
            <ProtectedRoute>
              <StudentLevelPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/:lang/:level/:topicSlug"
          element={
            <ProtectedRoute>
              <LessonPreviewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/:lang/:level/:topicSlug/lesson"
          element={
            <ProtectedRoute>
              <LessonPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <StaffRoute>
                <AdminPanel />
              </StaffRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/tickets"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AdminTicketsPage />
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/tickets/new"
          element={
            <ProtectedRoute>
              <TicketCreatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/telegram-link"
          element={
            <ProtectedRoute>
              <TelegramLinkPage />
            </ProtectedRoute>
          }
        />
        <Route path="/" element={<Navigate to={levelPath} replace />} />
        <Route path="*" element={<Navigate to={levelPath} replace />} />
      </Routes>
    </div>
  );
};

function App() {
  return (
    <Router>
      <ThemeProvider>
        <LanguageProvider>
          <LevelProvider>
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          </LevelProvider>
        </LanguageProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
